import { useEffect, useSyncExternalStore, type ReactNode } from 'react';
import { Portal } from '../../core/Portal/Portal.js';
import { useNetworkStore } from '@repletoryreach/sdk-utils/store';
import { nprogress } from './nprogress-store.js';
import './NavigationProgress.css';

export interface NavigationProgressProps {
  /** Bar color. Defaults to `--rr-color-primary`. */
  color?: string;
  /** Bar height in px. Default 2. */
  height?: number;
  /** Z-index override. Defaults to `--rr-z-toast`. */
  zIndex?: number;
  /** Render into a different portal target. */
  portalTarget?: HTMLElement | string;
  /**
   * When true (default), the bar auto-syncs with sdk-utils
   * `useNetworkStore().inFlight`: every in-flight request bumps the
   * refcount, every completion drops it. Set false to only respond to
   * imperative `nprogress.*` calls.
   */
  withNetwork?: boolean;
}

/**
 * Top-of-page progress bar. Mount once at the root (typically inside
 * `<UIProvider>` or `<Providers>`). Imperative API on `nprogress`:
 *
 *   import { nprogress } from '@repletoryreach/sdk-ui/nprogress';
 *   nprogress.start();         // begin
 *   nprogress.set(0.4);        // jump to 40%
 *   nprogress.inc(0.1);        // +10%
 *   nprogress.complete();      // finish (refcount-aware)
 *   nprogress.done();          // force finish
 *
 * When `withNetwork` is on, every `useNetworkStore().withTracking()` call
 * implicitly bumps and finishes the bar: no manual wiring needed for
 * fetch / SignalR / Hangfire roundtrips.
 */
export function NavigationProgress({
  color, height = 2, zIndex, portalTarget, withNetwork = true
}: NavigationProgressProps): ReactNode {
  const state = useSyncExternalStore(
    nprogress.subscribe,
    nprogress.getState,
    () => nprogress.getState()
  );

  // Auto-track sdk-utils' network in-flight counter so any fetch wrapped
  // by `useNetworkStore().withTracking(...)` triggers the bar.
  const inFlight = useNetworkStore((s) => s.inFlight);
  useEffect(() => {
    if (!withNetwork) return;
    if (inFlight > 0 && nprogress.getState().value === null) {
      nprogress.start();
    } else if (inFlight === 0 && nprogress.getState().value !== null) {
      nprogress.done();
    }
  }, [inFlight, withNetwork]);

  if (state.value === null) return null;

  return (
    <Portal target={portalTarget}>
      <div
        className="rr-nprogress"
        role="progressbar"
        aria-valuenow={Math.round(state.value * 100)}
        aria-valuemin={0}
        aria-valuemax={100}
        style={{
          height,
          zIndex: zIndex ?? 'var(--rr-z-toast)' as unknown as number
        }}
      >
        <div
          className="rr-nprogress__bar"
          style={{
            width: `${state.value * 100}%`,
            background: color
          }}
        />
      </div>
    </Portal>
  );
}
